import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Briefcase, Plus, MapPin, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const SBL_LOGO = "/sbl-logo.jpeg";

function CareersContent({ user }) {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [form, setForm] = useState({ title: "", department: "", location: "", job_type: "full_time", description: "", requirements: "", status: "open" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const canManage = ["super_admin", "admin", "hr"].includes(user?.role);

  const fetchCareers = () => {
    api.get("/careers").then(r => { setCareers(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchCareers(); }, []);

  const handleCreate = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/careers/${editingId}`, form);
        toast.success("Job posting updated");
      } else {
        await api.post("/careers", form);
        toast.success("Job posting created");
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ title: "", department: "", location: "", job_type: "full_time", description: "", requirements: "", status: "open" });
      fetchCareers();
    } catch (e) { toast.error("Failed to save posting"); }
    setSaving(false);
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      title: c.title || "", department: c.department || "", location: c.location || "",
      job_type: c.job_type || "full_time", description: c.description || "",
      requirements: c.requirements || "", status: c.status || "open",
    });
    setDialogOpen(true);
  };

  const deleteCareer = async (c) => {
    if (!window.confirm(`Delete vacancy "${c.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/careers/${c.id}`);
      toast.success("Vacancy deleted");
      fetchCareers();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleApply = async (careerId) => {
    try {
      await api.post(`/careers/${careerId}/apply`);
      toast.success("Application submitted!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply"); }
  };

  const typeLabels = { full_time: "Full Time", part_time: "Part Time", contract: "Contract", internship: "Internship" };

  return (
      <div className="p-6 md:p-8 lg:p-12" data-testid="careers-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Opportunities</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Open Vacancies
            </h1>
            <p className="text-sm text-slate-600 mt-1">Explore open positions at School Builder Lab Africa</p>
          </div>
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="create-career-btn">
                  <Plus size={16} weight="bold" /> Post Job
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg rounded-none">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Post Job Opening</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto pr-2">
                  <div>
                    <Label className="text-sm">Job Title</Label>
                    <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-none" data-testid="career-title-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Department</Label>
                      <Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="mt-1 rounded-none" placeholder="e.g. Education" />
                    </div>
                    <div>
                      <Label className="text-sm">Location</Label>
                      <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="mt-1 rounded-none" placeholder="e.g. Lagos" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Job Type</Label>
                    <Select value={form.job_type} onValueChange={v => setForm({...form, job_type: v})}>
                      <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full Time</SelectItem>
                        <SelectItem value="part_time">Part Time</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="internship">Internship</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Description</Label>
                    <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 rounded-none" rows={3} />
                  </div>
                  <div>
                    <Label className="text-sm">Requirements</Label>
                    <Textarea value={form.requirements} onChange={e => setForm({...form, requirements: e.target.value})} className="mt-1 rounded-none" rows={3} placeholder="List requirements, one per line" />
                  </div>
                  <Button onClick={handleCreate} disabled={saving || !form.title} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-career-btn">
                    {saving ? "Posting..." : "Post Job"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-100 animate-pulse" />)}</div>
        ) : careers.filter(c => canManage || c.status === "open").length === 0 ? (
          <div className="text-center py-16">
            <Briefcase size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No openings at this time</p>
          </div>
        ) : (
          <div className="space-y-4">
            {careers.filter(c => canManage || c.status === "open").map((c, idx) => (
              <Card key={c.id} className={`border border-slate-200 rounded-none shadow-sm hover:border-slate-400 transition-all cursor-pointer ${selectedCareer?.id === c.id ? "border-blue-800" : ""}`} data-testid={`career-card-${idx}`} onClick={() => setSelectedCareer(selectedCareer?.id === c.id ? null : c)}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={c.status === "open" ? "default" : "secondary"} className="rounded-none text-xs capitalize">{c.status}</Badge>
                        <Badge variant="outline" className="rounded-none text-xs">{typeLabels[c.job_type] || c.job_type}</Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.title}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        {c.department && <span>{c.department}</span>}
                        {c.location && <span className="flex items-center gap-1"><MapPin size={14} /> {c.location}</span>}
                      </div>
                    </div>
                    {user?.role === "student" && c.status === "open" && (
                      <Button onClick={(e) => { e.stopPropagation(); handleApply(c.id); }} size="sm" className="bg-gold-500 text-slate-950 hover:bg-gold-600 rounded-none" data-testid={`apply-career-${idx}`}>
                        Apply
                      </Button>
                    )}
                    {canManage && (
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button onClick={() => startEdit(c)} size="sm" variant="ghost" className="rounded-none gap-1 text-blue-800 hover:bg-blue-50" data-testid={`edit-career-${idx}`}>
                          Edit
                        </Button>
                        <Button onClick={() => deleteCareer(c)} size="sm" variant="ghost" className="rounded-none gap-1 text-red-700 hover:bg-red-50" data-testid={`delete-career-${idx}`}>
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                  {selectedCareer?.id === c.id && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      {c.description && <div className="mb-3"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Description</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{c.description}</p></div>}
                      {c.requirements && <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Requirements</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{c.requirements}</p></div>}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
  );
}

export default function CareersPage() {
  const { user } = useAuth();

  if (user) {
    return <Layout><CareersContent user={user} /></Layout>;
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="School Builder Lab Africa" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              School Builder Lab Africa
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="rounded-none text-sm font-medium text-slate-700 hover:text-slate-950 px-3">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none text-sm font-medium px-3 sm:px-5">Register</Button>
            </Link>
          </div>
        </div>
      </header>
      <div className="max-w-5xl mx-auto">
        <CareersContent user={null} />
      </div>
    </div>
  );
}
